import type { Operation } from '@apollo/client';
import { ApolloLink } from '@apollo/client/link/core';
import { RetryLink } from '@apollo/client/link/retry';
import { Kind } from 'graphql';
import type { OperationDefinitionNode } from 'graphql/language/ast';

/**
 * Statuses meaning the request never reached a healthy origin, so replaying it
 * can produce a different answer.
 *
 * 500 is excluded: that is the application responding with a real error, and
 * repeating it only multiplies load.
 */
const GATEWAY_STATUS_CODES = [502, 503, 504];

export interface GatewayRetryOptions {
    /** Total attempts including the first, so 2 allows one replay. */
    maxAttempts: number;
    initialDelayMs: number;
    maxDelayMs: number;
    /**
     * Operation names that must never be replayed even when they are reads.
     * Error-reporting operations belong here, or a failing report retries
     * itself.
     */
    excludedOperations?: string[];
    /**
     * Give up on replaying once this much time has already been spent on the
     * operation, measured from the moment it entered this link.
     *
     * A 502/503 comes back immediately, but a 504 is the gateway reporting that
     * it timed out waiting — so the first attempt may already have cost the ALB
     * idle timeout (60s in this deployment). Replaying that turns one slow
     * response into two, which on the server is a doubled time-to-first-byte.
     * The budget keeps the fast-failure case (an instance rotating out of the
     * target group, which is the case this link exists for) and drops the
     * replay once waiting again would cost the caller more than it can win.
     *
     * Omit to replay regardless of elapsed time.
     */
    retryBudgetMs?: number;
}

/**
 * Reads the operation type off the parsed document rather than through
 * `@apollo/client/utilities`, matching how `skipCacheDirective` inspects
 * operations. Consumers include a server bundle that does not otherwise pull in
 * that subpath.
 *
 * Only a plain query qualifies. Mutations are not idempotent, subscriptions do
 * not replay meaningfully, and a document with no operation definition is
 * treated as unsafe rather than guessed at.
 */
const isReadOperation = (operation: Operation): boolean => {
    const definition = operation.query.definitions.find(
        (candidate) => candidate.kind === Kind.OPERATION_DEFINITION,
    ) as OperationDefinitionNode | undefined;

    return definition?.operation === 'query';
};

/**
 * A network error carrying no `statusCode` is either an aborted in-flight
 * request or a transport failure the fetch layer has already wrapped; neither
 * is distinguishable from a client giving up, and replaying those would re-fire
 * a request per navigation.
 */
const isGatewayFailure = (error: unknown): boolean => {
    const statusCode = (error as { statusCode?: number } | null)?.statusCode;

    return (
        typeof statusCode === 'number' &&
        GATEWAY_STATUS_CODES.includes(statusCode)
    );
};

/**
 * Replays reads that failed at the gateway rather than at the application.
 *
 * Belongs downstream of the `onError` link so error reporting observes the
 * final outcome rather than every attempt, and as far down the chain as the
 * consumer's routing allows, so links that record metrics or forward response
 * headers run once per operation rather than once per attempt.
 */
export const createGatewayRetryLink = ({
    maxAttempts,
    initialDelayMs,
    maxDelayMs,
    excludedOperations = [],
    retryBudgetMs,
}: GatewayRetryOptions): ApolloLink => {
    // `RetryLink` hands `retryIf` the same `Operation` instance on every
    // attempt, so the entry timestamp can hang off it without a context round
    // trip. Weak so a discarded operation is not retained.
    const enteredAt = new WeakMap<Operation, number>();

    const stampEntryTime = new ApolloLink((operation, forward) => {
        enteredAt.set(operation, Date.now());

        return forward(operation);
    });

    const withinBudget = (operation: Operation): boolean => {
        if (retryBudgetMs === undefined) return true;

        const started = enteredAt.get(operation);
        // No stamp means the link chain was bypassed; fall back to allowing the
        // replay rather than silently disabling retries.
        if (started === undefined) return true;

        return Date.now() - started < retryBudgetMs;
    };

    const retryLink = new RetryLink({
        delay: {
            initial: initialDelayMs,
            max: maxDelayMs,
            jitter: true,
        },
        attempts: {
            max: maxAttempts,
            retryIf: (error, operation) =>
                !excludedOperations.includes(operation.operationName) &&
                isReadOperation(operation) &&
                isGatewayFailure(error) &&
                withinBudget(operation),
        },
    });

    return ApolloLink.from([stampEntryTime, retryLink]);
};
